import { useEffect, useRef, useState } from 'react'
import { formatCell, type GridCellType, type GridCellValue } from '../../lib/grid'

interface GridCellProps {
  value: GridCellValue
  type: GridCellType
  /** Focused in the roving-tabindex sense — exactly one cell of the grid carries tabIndex 0. */
  focused: boolean
  editing: boolean
  /** The character that OPENED the edit, when typing started it. Present = the member began by
   *  typing, so it replaces the cell (the spreadsheet reflex); absent = Enter/double-click, which
   *  keeps what is there. Passed as state rather than replayed onto the DOM afterwards — a
   *  synthetic `input` event on a controlled React field is a race, this is not. */
  seed?: string
  readOnly: boolean
  /** Accessible label for the cell, read as "<column>, row N". */
  label: string
  /** Display name of whoever is working on this cell right now (a claim or a remote caret). */
  busyBy?: string
  onFocus: () => void
  onStartEdit: () => void
  /** Commit and leave edit mode. `null` clears the cell. */
  onCommit: (raw: string) => void
  onCancelEdit: () => void
  onKeyDown: (e: React.KeyboardEvent) => void
}

/**
 * May the roving target take DOM focus? Only while focus is in this grid already (an arrow key, a
 * committed edit) or nowhere: becoming the fallback target must not steal it from elsewhere — the
 * lock notice's Release button (AB#301) drops the focused cell from outside the grid.
 */
function focusIsOurs(cell: HTMLElement | null): boolean {
  const active = document.activeElement
  if (!active || active === document.body) return true
  return !!cell?.closest('table')?.contains(active)
}

/**
 * One cell of the grid. A `<td>` that is focusable when it is the roving-tabindex target, and
 * swaps to a real `<textarea>` while being edited.
 *
 * A TEXTAREA, not an input, and that is the whole point of the part kind: a compliance matrix
 * holds a paragraph-length response per requirement, which a GFM pipe-table cannot express at all.
 * Enter commits (the common case is one line); Shift+Enter inserts a newline.
 */
export function GridCell({
  value,
  type,
  focused,
  editing,
  seed,
  readOnly,
  label,
  busyBy,
  onFocus,
  onStartEdit,
  onCommit,
  onCancelEdit,
  onKeyDown,
}: GridCellProps) {
  const [draft, setDraft] = useState('')
  const cellRef = useRef<HTMLTableCellElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Entering edit mode seeds the draft ONCE: from the keystroke that opened it (typing replaces
  // the cell), else from the current value (Enter/double-click keeps it). Re-seeding on every
  // render would fight the member's typing whenever a teammate's unrelated change re-rendered
  // the grid, so this is deliberately not keyed on `value`.
  useEffect(() => {
    if (editing) setDraft(seed ?? formatCell(value))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above.
  }, [editing])

  useEffect(() => {
    if (editing) inputRef.current?.focus()
    else if (focused && document.activeElement !== cellRef.current && focusIsOurs(cellRef.current)) cellRef.current?.focus()
  }, [editing, focused])

  const numeric = type !== 'text' && type !== 'date'

  return (
    <td
      ref={cellRef}
      role="gridcell"
      className={`gcell${numeric ? ' num' : ''}${busyBy ? ' busy' : ''}`}
      // Roving tabindex: one Tab stop for the WHOLE grid, arrows move within it. A tabIndex on
      // every cell would make a 200-row pricing grid an 800-stop obstacle for a keyboard user.
      tabIndex={focused ? 0 : -1}
      aria-label={label}
      aria-readonly={readOnly || undefined}
      {...(busyBy ? { 'data-busy-by': busyBy } : {})}
      onFocus={onFocus}
      onDoubleClick={readOnly ? undefined : onStartEdit}
      onKeyDown={onKeyDown}
    >
      {editing ? (
        <textarea
          ref={inputRef}
          className="gcell-input"
          rows={1}
          aria-label={label}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => onCommit(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              onCommit(draft)
            } else if (e.key === 'Escape') {
              e.preventDefault()
              onCancelEdit()
            }
            // Every other key — including Tab and the arrows — belongs to the textarea while
            // editing, so caret movement inside a long compliance answer is not stolen by the grid.
            e.stopPropagation()
          }}
        />
      ) : (
        <span className="gcell-text">{formatCell(value)}</span>
      )}
      {busyBy && !editing && (
        <span className="gcell-busy" aria-hidden="true" title={busyBy}>
          ◆
        </span>
      )}
    </td>
  )
}
